import { BillingFormValues, Country } from "@ecologi/shared";

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
const predictionCache = new Map<string, google.maps.places.PlacePrediction>();

export async function fetchPlacesAutocomplete(
  input: string,
  country?: Country
) {
  if (typeof google === "undefined" || !google.maps) {
    console.warn(
      "Google Maps API not loaded - address autocomplete unavailable"
    );
    return [];
  }

  const { AutocompleteSessionToken, AutocompleteSuggestion } =
    (await google.maps.importLibrary("places")) as google.maps.PlacesLibrary;

  if (!sessionToken) {
    sessionToken = new AutocompleteSessionToken();
  }

  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      includedRegionCodes: country ? [country.toLowerCase()] : ["gb"],
      sessionToken,
    });

  predictionCache.clear();
  return suggestions.map((suggestion) => {
    const placeId = suggestion.placePrediction?.placeId || "";
    if (suggestion.placePrediction && placeId) {
      predictionCache.set(placeId, suggestion.placePrediction);
    }
    return {
      placeId,
      description: suggestion.placePrediction?.text?.toString() || "",
    };
  });
}

async function fetchPlaceDetails(placeId: string) {
  const prediction = predictionCache.get(placeId);

  let addressComponents: google.maps.places.AddressComponent[];

  if (prediction) {
    const place = prediction.toPlace();
    await place.fetchFields({ fields: ["addressComponents"] });
    addressComponents = place.addressComponents || [];
  } else {
    const { Place } = (await google.maps.importLibrary(
      "places"
    )) as google.maps.PlacesLibrary;
    const place = new Place({ id: placeId });
    await place.fetchFields({ fields: ["addressComponents"] });
    addressComponents = place.addressComponents || [];
  }

  sessionToken = null;
  predictionCache.clear();

  return addressComponents;
}

export async function fetchAndParsePlace(
  placeId: string
): Promise<Partial<BillingFormValues>> {
  const addressComponents = await fetchPlaceDetails(placeId);
  return parseAddressComponents(addressComponents);
}

export function parseAddressComponents(
  addressComponents: google.maps.places.AddressComponent[]
): Partial<BillingFormValues> {
  const getComponent = (type: string, format: "long" | "short" = "long") => {
    const component = addressComponents.find((c) => c.types.includes(type));
    return format === "long" ? component?.longText : component?.shortText;
  };

  const premise = getComponent("premise");
  const streetNumber = getComponent("street_number");
  const route = getComponent("route");
  const subpremise = getComponent("subpremise");
  const neighborhood = getComponent("neighborhood");
  const postalCode = getComponent("postal_code");
  const countryCode = getComponent("country", "short") as Country;

  const locality =
    getComponent("locality") ||
    getComponent("postal_town") ||
    getComponent("sublocality_level_1") ||
    getComponent("administrative_area_level_2");

  const streetAddress = streetNumber
    ? [streetNumber, route].filter(Boolean).join(" ")
    : "";
  const primaryAddress = streetAddress || premise || route;
  const billingAddress1 = primaryAddress || subpremise || "";
  const usedPremiseAsPrimary = !streetAddress && premise;
  const billingAddress2 =
    (usedPremiseAsPrimary && route) ||
    (primaryAddress && subpremise) ||
    neighborhood ||
    "";

  return {
    billingAddress1,
    billingAddress2,
    billingCity: locality || "",
    billingPostcode: postalCode || "",
    billingCountry: countryCode || "",
  };
}
